import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set } from 'firebase/database';
import { database } from '../../../../firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

// Dark theme styles
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
  },
  modal: {
    background: '#0B1C34',
    border: '1px solid rgba(86, 141, 237, 0.2)',
    borderRadius: '12px',
    width: '700px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    color: '#FFFFFF',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(86, 141, 237, 0.15)',
  },
  title: {
    margin: 0,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '24px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  body: {
    padding: '24px',
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  readOnly: {
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '16px',
  },
  textarea: {
    width: '100%',
    minHeight: '80px',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid rgba(86, 141, 237, 0.15)',
  },
  closeButton: {
    padding: '10px 20px',
    background: '#568ded',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const ApprovePTOModal = ({ employeeId, companyUID, employeeName, ptoRequests, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [action, setAction] = useState(null); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);

  const pendingRequests = Object.entries(ptoRequests || {})
    .filter(([_, req]) => req.status === 'pending')
    .map(([id, req]) => ({ id, ...req }));

  const handleApprove = async (requestId) => {
    setSelectedRequestId(requestId);
    setAction('approve');
    setComment('');
  };

  const handleReject = async (requestId) => {
    setSelectedRequestId(requestId);
    setAction('reject');
    setComment('');
  };

  const selectedRequest = pendingRequests.find(req => req.id === selectedRequestId);

  const handleSubmit = async () => {
    if (!selectedRequestId || !action) return;

    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/approve-pto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          requestId: selectedRequestId,
          action, // 'approve' or 'reject'
          comment: comment.trim() || undefined,
          approvedByUID: currentUser?.uid,
          approvedByName: currentUser?.displayName || currentUser?.email || 'Manager'
        })
      });

      const result = await response.json();
      if (result.success) {
        // Send Notification to Employee (Firebase)
        const notifTitle = `PTO Request ${action === 'approve' ? 'Approved' : 'Rejected'}`;
        const notifMessage = `Your PTO request for ${selectedRequest?.startDate} to ${selectedRequest?.endDate} has been ${action}ed by ${currentUser?.displayName || 'Manager'}.`;
        const notifType = action === 'approve' ? 'success' : 'error';
        
        try {
          const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
          await set(notifRef, {
            title: notifTitle,
            message: notifMessage,
            type: notifType,
            timestamp: Date.now(),
            read: false
          });
        } catch (err) {
          console.error('Failed to send notification', err);
        }

        // Send Notification to Employee (Email)
        try {
          await fetch(`${BACKEND_URL}/send-notification-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              companyId: companyUID,
              employeeId,
              subject: notifTitle,
              message: notifMessage,
              type: notifType
            })
          });
        } catch (err) {
          console.error('Email notification failed:', err);
        }

        alert(`PTO request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
        onSuccess?.();
        onClose();
      } else {
        alert(`Failed to ${action} PTO request: ` + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Approve PTO failed:', error);
      alert(`Failed to ${action} PTO request`);
    } finally {
      setProcessing(false);
      setSelectedRequestId(null);
      setAction(null);
      setComment('');
    }
  };

  if (pendingRequests.length === 0) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Approve PTO</h2>
            <button style={styles.closeBtn} onClick={onClose}>&times;</button>
          </div>
          <div style={{ ...styles.body, textAlign: 'center', padding: '40px' }}>
            <i className="fa-solid fa-check-circle" style={{ fontSize: '48px', color: '#40EB6E', marginBottom: '16px' }}></i>
            <div style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 600 }}>
              No pending PTO requests
            </div>
            <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>
              All PTO requests have been processed
            </div>
          </div>
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.closeButton}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Approve PTO Requests</h2>
          <button style={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div style={styles.body}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Employee</label>
            <div style={styles.readOnly}>
              {employeeName || 'Unknown Employee'}
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={{ ...styles.label, marginBottom: '12px' }}>
              Pending Requests ({pendingRequests.length})
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: '16px',
                    backgroundColor: selectedRequestId === req.id ? 'rgba(86, 141, 237, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                    border: `2px solid ${selectedRequestId === req.id ? '#568ded' : 'rgba(255, 255, 255, 0.08)'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => {
                    setSelectedRequestId(req.id);
                    setAction(null);
                    setComment('');
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                        {(req.type || 'vacation').charAt(0).toUpperCase() + (req.type || 'vacation').slice(1)} Leave
                      </div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>
                        {req.startDate} to {req.endDate} • {req.days || 0} day{req.days !== 1 ? 's' : ''}
                      </div>
                      {req.comment && (
                        <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '8px', fontStyle: 'italic' }}>
                          "{req.comment}"
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(req.id);
                        }}
                        disabled={processing}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#40EB6E',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#000',
                          cursor: processing ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(req.id);
                        }}
                        disabled={processing}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#EB4040',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#FFFFFF',
                          cursor: processing ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedRequest && action && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: action === 'approve' ? 'rgba(64, 235, 110, 0.1)' : 'rgba(235, 64, 64, 0.1)',
              border: `1px solid ${action === 'approve' ? 'rgba(64, 235, 110, 0.3)' : 'rgba(235, 64, 64, 0.3)'}`,
              borderRadius: '8px'
            }}>
              <div style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                {action === 'approve' ? 'Approve' : 'Reject'} Request
              </div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                {selectedRequest.startDate} to {selectedRequest.endDate} • {selectedRequest.days || 0} days
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={`Add a comment (optional)...`}
                style={styles.textarea}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button
                  onClick={() => {
                    setSelectedRequestId(null);
                    setAction(null);
                    setComment('');
                  }}
                  disabled={processing}
                  style={{
                    ...styles.cancelBtn,
                    opacity: processing ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={processing}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: action === 'approve' ? '#40EB6E' : '#EB4040',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: action === 'approve' ? '#000' : '#FFFFFF',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    opacity: processing ? 0.7 : 1,
                  }}
                >
                  {processing ? 'Processing...' : `${action === 'approve' ? 'Approve' : 'Reject'} Request`}
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={styles.footer}>
          <button
            onClick={onClose}
            disabled={processing}
            style={{
              ...styles.cancelBtn,
              opacity: processing ? 0.5 : 1,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovePTOModal;
